import React from 'react';
import { NodeResizer, useReactFlow, type NodeProps } from '@xyflow/react';
import { GripHorizontal } from 'lucide-react';
import type { FlowNode } from '../graph/types';

const COLORES: Record<string, string> = {
  amarillo: 'bg-[#FFF4B8] border-[#F2DF8A]',
  celeste: 'bg-[#DDF3FF] border-[#B7E1FA]',
  rosa: 'bg-[#FFE3EC] border-[#F9BFD1]',
  verde: 'bg-[#E1F8EA] border-[#B9E9CB]',
};

/** Papelito de contexto. Se edita en la misma tarjeta (el textarea no arrastra). */
export function NodoNota({ id, data, selected }: NodeProps<FlowNode>) {
  const { updateNodeData } = useReactFlow();
  const color = COLORES[String(data.params.color)] ?? COLORES.amarillo;
  return (
    <div
      className={`flex h-full w-full flex-col rounded-xl border p-3 shadow-[0_4px_14px_rgb(var(--brand-black-rgb)_/_0.08)] ${color} ${
        selected ? 'ring-2 ring-brand-primary/30' : ''
      }`}
      style={{ minWidth: 160, minHeight: 100 }}
    >
      <NodeResizer minWidth={160} minHeight={100} isVisible={selected} lineClassName="!border-brand-primary/40" handleClassName="!h-3 !w-3 !rounded-full !border-2 !border-brand-primary !bg-white" />
      {/* Barra para mover la nota: el papel es casi todo textarea (nodrag), así que sin
          esto al arrastrar aparecía el cursor de texto y la nota no se movía. */}
      <div className="-mx-3 -mt-3 mb-1.5 flex cursor-grab items-center justify-center rounded-t-xl py-1 text-brand-black/30 hover:text-brand-black/60 active:cursor-grabbing" title="Arrastrá para mover la nota">
        <GripHorizontal className="h-4 w-4" />
      </div>
      <textarea
        className="nodrag min-h-0 w-full flex-1 resize-none bg-transparent text-[12.5px] leading-snug text-brand-black/85 outline-none placeholder:text-brand-black/35"
        placeholder="Escribí una nota…"
        value={String(data.params.texto ?? '')}
        onChange={(e) => updateNodeData(id, { params: { ...data.params, texto: e.target.value } })}
      />
      <div className="mt-1 flex gap-1.5">
        {Object.keys(COLORES).map((c) => (
          <button
            key={c}
            type="button"
            className={`nodrag h-3.5 w-3.5 rounded-full border border-black/10 ${COLORES[c].split(' ')[0]} ${
              data.params.color === c ? 'ring-1 ring-brand-black/40' : ''
            }`}
            title={c}
            onClick={() => updateNodeData(id, { params: { ...data.params, color: c } })}
          />
        ))}
      </div>
    </div>
  );
}

export default React.memo(NodoNota);
